import {
  CreateTaskData,
  Task,
  TaskConfigurations,
  TaskDropdownOption,
  TaskProgressUpdate,
  TasksListParams,
  TasksListResponse,
  TaskStatistics,
  TaskUser,
  UpdateTaskData,
} from '../types';
import apiService from './api';

// Note: Mock data removed - now using real API endpoints

class TaskService {
  // Get tasks with pagination and filtering
  async getTasks(
    projectId: string,
    params: TasksListParams = {}
  ): Promise<{ success: boolean; data?: TasksListResponse; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });

      const url = `/projects/${projectId}/tasks?${queryParams.toString()}`;
      const response = await apiService.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return {
        success: false,
        message: 'Failed to fetch tasks',
      };
    }
  }

  // Get single task
  async getTask(taskId: string): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task:', error);
      return {
        success: false,
        message: 'Failed to fetch task',
      };
    }
  }

  // Create task
  async createTask(
    projectId: string,
    data: CreateTaskData
  ): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.post(`/projects/${projectId}/tasks`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      return {
        success: false,
        message: 'Failed to create task',
      };
    }
  }

  // Update task
  async updateTask(
    taskId: string,
    data: UpdateTaskData
  ): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.put(`/tasks/${taskId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      return {
        success: false,
        message: 'Failed to update task',
      };
    }
  }

  // Delete task
  async deleteTask(taskId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiService.delete(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting task:', error);
      return {
        success: false,
        message: 'Failed to delete task',
      };
    }
  }

  // Get task statistics
  async getTaskStatistics(
    projectId: string
  ): Promise<{ success: boolean; data?: TaskStatistics; message?: string }> {
    try {
      const response = await apiService.get(`/projects/${projectId}/tasks/statistics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task statistics:', error);
      return {
        success: false,
        message: 'Failed to fetch task statistics',
      };
    }
  }

  // Get available users for assignment
  async getAssignableUsers(
    projectId: string
  ): Promise<{ success: boolean; data?: TaskUser[]; message?: string }> {
    try {
      const response = await apiService.get(`/projects/${projectId}/tasks/assignable-users`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assignable users:', error);
      return {
        success: false,
        message: 'Failed to fetch users',
      };
    }
  }

  // Add comment to task
  async addComment(
    taskId: string,
    content: string
  ): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.post(`/tasks/${taskId}/comments`, { content });
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      return {
        success: false,
        message: 'Failed to add comment',
      };
    }
  }

  // Add attachment to task
  async addAttachment(
    taskId: string,
    fileName: string,
    fileUrl: string,
    fileType: string,
    fileSize: number
  ): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.post(`/tasks/${taskId}/attachments`, {
        fileName,
        fileUrl,
        fileType,
        fileSize,
      });
      return response.data;
    } catch (error) {
      console.error('Error adding attachment:', error);
      return {
        success: false,
        message: 'Failed to add attachment',
      };
    }
  }

  // Get task configurations - now uses generic config service
  async getTaskConfigurations(): Promise<{
    success: boolean;
    data?: TaskConfigurations;
    message?: string;
  }> {
    try {
      const response = await apiService.get('/tasks/configurations');
      return response.data;
    } catch (error) {
      console.error('Error fetching task configurations:', error);
      return {
        success: false,
        message: 'Failed to fetch task configurations',
      };
    }
  }

  // Legacy method - now delegates to generic config service
  async getAllDropdownConfigurations(): Promise<{
    priorities: TaskDropdownOption[];
    categories: TaskDropdownOption[];
    statuses: TaskDropdownOption[];
    progressStatuses: TaskDropdownOption[];
    roles: TaskDropdownOption[];
  }> {
    // Import here to avoid circular dependency
    const { default: configService } = await import('./configService');
    return configService.getTaskConfigurations();
  }

  // Legacy methods for backward compatibility - delegate to generic config service
  async getStatusOptions(): Promise<TaskDropdownOption[]> {
    const { default: configService } = await import('./configService');
    return configService.getConfiguration('task-statuses');
  }

  async getPriorityOptions(): Promise<TaskDropdownOption[]> {
    const { default: configService } = await import('./configService');
    return configService.getConfiguration('task-priorities');
  }

  async getCategoryOptions(): Promise<TaskDropdownOption[]> {
    const { default: configService } = await import('./configService');
    return configService.getConfiguration('task-categories');
  }

  async getProgressStatusOptions(): Promise<TaskDropdownOption[]> {
    const { default: configService } = await import('./configService');
    return configService.getConfiguration('task-progress-status');
  }

  async getProjectRoleOptions(): Promise<TaskDropdownOption[]> {
    const { default: configService } = await import('./configService');
    return configService.getConfiguration('project-roles');
  }

  // Progress tracking methods
  async getTaskProgressHistory(
    taskId: string
  ): Promise<{ success: boolean; data?: TaskProgressUpdate[]; message?: string }> {
    try {
      const response = await apiService.get(`/tasks/${taskId}/progress-history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task progress history:', error);
      return {
        success: false,
        message: 'Failed to fetch task progress history',
      };
    }
  }

  async updateTaskProgress(
    taskId: string,
    progressData: {
      progress: number;
      progressStatus: string;
      details: string;
      taskStatus?: string;
    }
  ): Promise<{ success: boolean; data?: Task; message?: string }> {
    try {
      const response = await apiService.post(`/tasks/${taskId}/progress`, progressData);
      return response.data;
    } catch (error) {
      console.error('Error updating task progress:', error);
      return {
        success: false,
        message: 'Failed to update task progress',
      };
    }
  }
}

export default new TaskService();
